import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * BrainForge Landing - Hero + Number One Son Software cross-promo
 */

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=DM+Sans:wght@400;500&display=swap');
  
  @keyframes orbRotate {
    0% { transform: rotateX(-20deg) rotateY(0deg); }
    100% { transform: rotateX(-20deg) rotateY(360deg); }
  }
  
  @keyframes orbPulse {
    0%, 100% { opacity: 0.7; }
    50% { opacity: 1; }
  }
  
  @keyframes orbFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-15px); }
  }

  .suite-orb-container {
    perspective: 1000px;
    animation: orbFloat 5s ease-in-out infinite;
  }
  
  .suite-orb {
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, 
      rgba(102, 126, 234, 0.5) 0%, 
      rgba(118, 75, 162, 0.35) 30%,
      rgba(248, 113, 113, 0.25) 60%,
      transparent 70%);
    position: relative;
    animation: orbRotate 25s linear infinite;
    box-shadow: 
      0 0 100px rgba(102, 126, 234, 0.35),
      0 0 150px rgba(118, 75, 162, 0.2),
      inset 0 0 100px rgba(102, 126, 234, 0.15);
  }
  
  .suite-orb::before {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    border: 2px solid rgba(102, 126, 234, 0.35);
    background: 
      radial-gradient(circle at 70% 70%, rgba(248, 113, 113, 0.25), transparent 50%),
      radial-gradient(circle at 30% 30%, rgba(102, 126, 234, 0.35), transparent 50%);
  }
  
  .suite-orb::after {
    content: '';
    position: absolute;
    inset: 20px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.15);
    animation: orbPulse 3s ease-in-out infinite;
  }
  
  .suite-orb-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(102, 126, 234, 0.25);
    animation: orbPulse 3s ease-in-out infinite;
  }
  
  .suite-orb-dots {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-image: 
      radial-gradient(circle, rgba(102, 126, 234, 0.9) 1px, transparent 1px),
      radial-gradient(circle, rgba(248, 113, 113, 0.7) 1px, transparent 1px);
    background-size: 24px 24px, 36px 36px;
    background-position: 0 0, 12px 12px;
    opacity: 0.45;
    animation: orbRotate 35s linear infinite reverse;
  }
`;

const AI_MODELS = [
  { name: 'Claude', color: '#667eea' },
  { name: 'GPT-4o', color: '#11998e' },
  { name: 'Grok', color: '#eb3349' },
  { name: 'DeepSeek', color: '#f093fb' }
];

const OTHER_TOOLS = [
  {
    name: 'ChimeIn',
    tagline: 'Impossible Conversations',
    description: 'Talk to anyone from history. Jesus. Einstein. Marilyn Monroe. Finally, you can ask.',
    icon: '🏛️',
    color: '#764ba2',
    url: 'https://chime-in-azure.vercel.app'
  },
  {
    name: 'MultiPowerAI',
    tagline: 'Full AI Dream Team',
    description: 'AI collaboration with full user control. Bring your own API keys.',
    icon: '💰',
    color: '#fbbf24',
    url: 'https://www.multipowerai.com'
  }
];

export default function Landing() {
  useEffect(() => {
    const existingStyle = document.getElementById('suite-hero-styles');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = 'suite-hero-styles';
      style.textContent = styles;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById('suite-hero-styles');
      if (style) document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0a0f 0%, #0f0f18 50%, #0a0a0f 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '40px 20px 60px',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: "'DM Sans', sans-serif"
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute',
        width: '900px',
        height: '900px',
        background: 'radial-gradient(circle, rgba(102, 126, 234, 0.12) 0%, transparent 60%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        top: '5%',
        pointerEvents: 'none'
      }} />

      {/* Orb */}
      <div className="suite-orb-container" style={{ marginBottom: '40px' }}>
        <div className="suite-orb">
          <div className="suite-orb-dots" />
          <div className="suite-orb-ring" style={{ inset: '40px' }} />
          <div className="suite-orb-ring" style={{ inset: '70px', animationDelay: '0.5s' }} />
          <div className="suite-orb-ring" style={{ inset: '100px', animationDelay: '1s' }} />
        </div>
      </div>

      {/* Headline */}
      <h1 style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: 'clamp(32px, 5vw, 52px)',
        fontWeight: '700',
        color: 'white',
        textAlign: 'center',
        marginBottom: '12px',
        letterSpacing: '-1px'
      }}>
        Four AI Minds. One Conversation.
      </h1>

      <p style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: '18px',
        color: 'rgba(255, 255, 255, 0.5)',
        marginTop: '10px',
        textAlign: 'center'
      }}>
        Claude. GPT. Grok. DeepSeek. Finally, they collaborate.
      </p>

      {/* HERO: BrainForge Card */}
      <div style={{
        marginTop: '50px',
        width: '100%',
        maxWidth: '420px',
        padding: '32px',
        background: 'rgba(102, 126, 234, 0.1)',
        border: '2px solid rgba(102, 126, 234, 0.3)',
        borderRadius: '20px',
        textAlign: 'center',
        position: 'relative'
      }}>
        <span style={{
          position: 'absolute',
          top: '-10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#667eea',
          color: 'white',
          padding: '4px 14px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>You're here</span>
        
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🚀</span>
        
        <div style={{
          color: '#667eea',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>Full AI Dream Team</div>
        
        <h2 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '32px',
          fontWeight: '700',
          color: 'white',
          margin: '0 0 12px 0'
        }}>BrainForge</h2>
        
        <p style={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '16px',
          lineHeight: '1.6',
          margin: 0
        }}>AI collaboration with full user control. Multiple AI models working together. API keys included — start instantly.</p>
      </div>

      {/* AI Models badges */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '40px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {AI_MODELS.map((ai) => (
          <span key={ai.name} style={{
            padding: '8px 16px',
            borderRadius: '20px',
            background: `${ai.color}15`,
            border: `1px solid ${ai.color}30`,
            color: ai.color,
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {ai.name}
          </span>
        ))}
      </div>

      {/* Auth Links */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        marginTop: '36px'
      }}>
        <Link 
          to="/signup" 
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '15px',
            fontWeight: '500',
            color: 'white',
            textDecoration: 'none',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            padding: '14px 32px',
            borderRadius: '24px',
            border: 'none',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
          }}
        >
          Get Started Free
        </Link>
        <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.3)' }}>or</span>
        <Link 
          to="/login" 
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '15px',
            fontWeight: '500',
            color: 'rgba(255, 255, 255, 0.5)',
            textDecoration: 'none',
            transition: 'all 0.2s ease'
          }}
        >
          Sign In
        </Link>
      </div>

      {/* Divider */}
      <div style={{
        width: '80px',
        height: '1px',
        background: 'rgba(255, 255, 255, 0.1)',
        margin: '60px auto 40px'
      }} />

      {/* Other Tools Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '24px'
      }}>
        <p style={{
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.35)',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          fontWeight: '500',
          margin: 0
        }}>Other Fantastic Tools by</p>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.5)',
          fontWeight: '600',
          margin: '6px 0 0 0'
        }}>Number One Son Software</p>
      </div>

      {/* Secondary Tool Cards */}
      <div style={{
        display: 'flex',
        gap: '20px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        {OTHER_TOOLS.map((tool) => (
          <a 
            key={tool.name}
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '20px 24px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px',
              textDecoration: 'none',
              width: '220px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
            }}
          >
            <span style={{ fontSize: '24px', display: 'block', marginBottom: '10px' }}>{tool.icon}</span>
            <div style={{
              color: tool.color,
              fontSize: '10px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px'
            }}>{tool.tagline}</div>
            <div style={{
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '8px'
            }}>{tool.name}</div>
            <p style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '12px',
              lineHeight: '1.5',
              margin: '0 0 10px 0'
            }}>{tool.description}</p>
            <span style={{
              color: tool.color,
              fontSize: '11px',
              fontWeight: '600'
            }}>Try for free →</span>
          </a>
        ))}
      </div>
    </div>
  );
}
